import React, { useState, useEffect } from 'react';

const CONSENT_KEY = 'tarr_cookie_consent';
const CONSENT_VERSION = '1.0';

export const getCookieConsent = () => {
    try {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (parsed.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
};

export const hasGivenConsent = (category = 'analytics') => {
    const consent = getCookieConsent();
    if (!consent) return false;
    return consent.preferences?.[category] === true;
};

const CookieConsent = ({ isDarkMode = false, onConsentChange = null }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState({
        essential: true,
        analytics: true,
        marketing: false,
    });

    const theme = {
        bg: isDarkMode ? 'rgba(20, 20, 20, 0.98)' : 'rgba(255, 255, 255, 0.98)',
        text: isDarkMode ? '#ffffff' : '#1a1a1a',
        textMuted: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
        accent: isDarkMode ? '#6366f1' : '#4f46e5',
        border: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        cardBg: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    };

    useEffect(() => {
        const consent = getCookieConsent();
        if (!consent) {
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const saveConsent = (prefs) => {
        const consentData = {
            version: CONSENT_VERSION,
            timestamp: new Date().toISOString(),
            preferences: prefs,
        };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
        setIsVisible(false);
        if (onConsentChange) onConsentChange(prefs);
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
    };

    const handleAcceptAll = () => {
        saveConsent({ essential: true, analytics: true, marketing: true });
    };

    const handleRejectNonEssential = () => {
        saveConsent({ essential: true, analytics: false, marketing: false });
    };

    if (!isVisible) return null;

    const categories = [
        { id: 'essential', name: 'Essential', desc: 'Required for site functionality', required: true },
        { id: 'analytics', name: 'Analytics', desc: 'Help us improve your experience', required: false },
        { id: 'marketing', name: 'Marketing', desc: 'Personalized content and ads', required: false },
    ];

    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'min(480px, calc(100% - 32px))',
            backgroundColor: theme.bg,
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            padding: showDetails ? '24px' : '20px',
            zIndex: 9999,
            fontFamily: "'Inter', sans-serif",
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.border}`,
        }}>
            {!showDetails ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>
                            🍪 Cookie Preferences
                        </span>
                        <p style={{ fontSize: '13px', color: theme.textMuted, margin: '6px 0 0' }}>
                            We use cookies to enhance your experience.{' '}
                            <button onClick={() => setShowDetails(true)} style={{
                                background: 'none', border: 'none', color: theme.accent,
                                cursor: 'pointer', padding: 0, fontSize: '13px', textDecoration: 'underline',
                            }}>Customize</button>
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={handleRejectNonEssential} style={{
                            padding: '10px 16px', backgroundColor: 'transparent',
                            border: `1px solid ${theme.border}`, borderRadius: '8px',
                            color: theme.text, fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                        }}>Reject</button>
                        <button onClick={handleAcceptAll} style={{
                            padding: '10px 20px', backgroundColor: theme.accent, border: 'none',
                            borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        }}>Accept All</button>
                    </div>
                </div>
            ) : (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: theme.text }}>🍪 Cookie Settings</span>
                        <button onClick={() => setShowDetails(false)} style={{
                            background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer',
                        }}>✕</button>
                    </div>
                    {categories.map((cat) => (
                        <div key={cat.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px', backgroundColor: theme.cardBg, borderRadius: '8px', marginBottom: '8px',
                        }}>
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: '500', color: theme.text }}>{cat.name}</div>
                                <div style={{ fontSize: '12px', color: theme.textMuted }}>{cat.desc}</div>
                            </div>
                            <button disabled={cat.required} onClick={() => setPreferences(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                                style={{
                                    width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: cat.required ? 'not-allowed' : 'pointer',
                                    backgroundColor: preferences[cat.id] ? theme.accent : 'rgba(128,128,128,0.3)', position: 'relative',
                                }}>
                                <div style={{
                                    width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff',
                                    position: 'absolute', top: '3px', left: preferences[cat.id] ? '21px' : '3px', transition: 'left 0.2s',
                                }} />
                            </button>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                        <button onClick={handleRejectNonEssential} style={{
                            padding: '10px 16px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`,
                            borderRadius: '8px', color: theme.text, fontSize: '13px', cursor: 'pointer',
                        }}>Reject All</button>
                        <button onClick={() => saveConsent(preferences)} style={{
                            padding: '10px 20px', backgroundColor: theme.accent, border: 'none',
                            borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        }}>Save</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CookieConsent;
